import { Controller } from "@hotwired/stimulus";

// Global state that survives Turbo navigation
window.appLocationState = window.appLocationState || {
  lat: null,
  lng: null,
  isTracking: false,
  watchId: null,
  cityName: "Locating...",
};

export default class extends Controller {
  static targets = ["userlocation", "flashmessage"];

  connect() {
    if (window.appLocationState.lat) {
      this.updateUI(window.appLocationState.lat, window.appLocationState.lng);
    }
    if (!window.appLocationState.isTracking) {
      this.startTracking();
    }
  }

  retry(event) {
    if (event) event.preventDefault();
    if (this.hasFlashmessageTarget) this.flashmessageTarget.innerHTML = "";
    this.startTracking();
  }

  startTracking() {
    if (window.NativeApp) {
      console.log("🚀 Using Native Bridge");
      window.appLocationState.isTracking = true;

      window.receiveNativeLocation = (lat, lng) => {
        this.updateGlobalState(lat, lng);
      };

      window.receiveNativeError = (msg) => {
        this.handleError({ message: msg, code: 99 });
      };

      window.NativeApp.requestLocation();
      return;
    }

    if (!navigator.geolocation) {
      this.handleError({ code: 0, message: "Geolocation not supported" });
      return;
    }

    window.appLocationState.isTracking = true;

    const options = {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0,
    };

    window.appLocationState.watchId = navigator.geolocation.watchPosition(
      (pos) =>
        this.updateGlobalState(pos.coords.latitude, pos.coords.longitude),
      (err) => this.handleError(err),
      options,
    );
  }

  updateGlobalState(lat, lng) {
    window.appLocationState.lat = lat;
    window.appLocationState.lng = lng;

    this.updateUI(lat, lng);

    window.dispatchEvent(
      new CustomEvent("user-location-updated", {
        detail: { latitude: lat, longitude: lng },
      }),
    );

    if (window.appLocationState.cityName === "Locating...") {
      this.fetchCityName(lat, lng);
    }

    // ------------------------------------------------------------------
    // AUTOMATIC FEED REFRESH LOGIC
    // ------------------------------------------------------------------
    const url = new URL(window.location.href);
    const urlLat = url.searchParams.get("lat");

    // ONLY auto-refresh if we are on the Home Page ("/") AND the GPS isn't already in the URL
    if (window.location.pathname === "/" && !urlLat) {
      console.log(
        "🎯 Initial GPS lock acquired! Refreshing feed automatically...",
      );

      // Preserve any existing search filters (?q=...) and add the GPS
      url.searchParams.set("lat", lat);
      url.searchParams.set("lng", lng);

      // Use { action: "replace" } so we don't clog up the user's "Back" button history
      Turbo.visit(url.toString(), { action: "replace" });
    }
  }

  updateUI(lat, lng) {
    if (this.hasUserlocationTarget) {
      this.userlocationTarget.textContent = window.appLocationState.cityName;
    }
  }

  handleError(error) {
    let msg = error.message || "Unknown error";
    if (error.code === 1)
      msg = "Please allow location access to see nearby deals.";

    console.warn("Location Error:", msg);
    window.appLocationState.isTracking = false;

    if (this.hasFlashmessageTarget) {
      this.flashmessageTarget.innerHTML = `
        <div class="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center px-4">
          <div class="bg-white rounded-2xl w-full max-w-md p-6 text-center shadow-xl">
            <h3 class="font-bold text-gray-900 mb-2">Location Needed</h3>
            <p class="text-gray-600 mb-6">${msg}</p>
            <button data-action="click->${this.identifier}#retry" class="bg-blue-600 text-white px-6 py-2 rounded-full">Try Again</button>
          </div>
        </div>`;
    }
  }

  fetchCityName(lat, lng) {
    fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`,
    )
      .then((res) => res.json())
      .then((data) => {
        const name =
          data.city ||
          data.locality ||
          data.principalSubdivision ||
          `${lat.toFixed(2)}, ${lng.toFixed(2)}`;
        window.appLocationState.cityName = name;
        this.updateUI(lat, lng);
      })
      .catch((e) => console.error("Geocoding failed", e));
  }

  disconnect() {
    window.appLocationState.isTracking = false;

    if (window.NativeApp && window.NativeApp.stopTracking) {
      window.NativeApp.stopTracking();
    }

    if (window.appLocationState.watchId) {
      navigator.geolocation.clearWatch(window.appLocationState.watchId);
      window.appLocationState.watchId = null;
    }
  }
}
